import { Capacitor } from '@capacitor/core';

/**
 * Platform detection utilities for web vs mobile
 */

export const isNativePlatform = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    // If Capacitor is not available, we're definitely on web
    return false;
  }
};

export const getPlatform = () => {
  try {
    return Capacitor.getPlatform();
  } catch {
    return 'web';
  }
};

export const isWeb = () => {
  return !isNativePlatform() || getPlatform() === 'web';
};

export const isIOS = () => {
  return isNativePlatform() && getPlatform() === 'ios';
};

export const isAndroid = () => {
  return isNativePlatform() && getPlatform() === 'android';
};

/**
 * Check if we're running in a mobile browser (for responsive design)
 */
export const isMobileBrowser = () => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = window.navigator.userAgent;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isSmallScreen = window.innerWidth <= 768;
  
  return isMobile || isSmallScreen;
};

/**
 * Check if device supports touch
 */
export const isTouchDevice = () => {
  if (typeof window === 'undefined') return false;
  
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

/**
 * Features that should be disabled on web
 */
export const shouldDisableFeature = (feature: 'notifications' | 'biometric' | 'camera' | 'fileExport' | 'contacts') => {
  if (isWeb()) {
    switch (feature) {
      case 'notifications':
        // Web push notifications could work, but disabled for now
        return true;
      case 'biometric':
        // Face ID / Touch ID not available on web
        return true;
      case 'camera':
        // Web camera API works via file input fallback
        return false;
      case 'fileExport':
        // File download works differently on web
        return false;
      case 'contacts':
        // Contact access not available on web
        return true;
      default:
        return false;
    }
  }
  
  return false;
};

/**
 * Get appropriate CSS classes for platform-specific styling
 */
export const getPlatformClasses = () => {
  const classes = [];
  
  if (isWeb()) {
    classes.push('platform-web');
  }
  
  if (isIOS()) {
    classes.push('platform-ios');
  }
  
  if (isAndroid()) {
    classes.push('platform-android');
  }
  
  if (isMobileBrowser()) {
    classes.push('mobile-browser');
  }
  
  if (isTouchDevice()) {
    classes.push('touch-device');
  }
  
  return classes.join(' ');
}; 